<template>
  <div v-if="isAdmin && !isUpdating" class="z-29 absolute left-4 top-4 flex gap-2">
    <gp-btn :label="t('ngp/interactivity_lock/button/toggle')" size="sm" @click="setToggleLock(!toggleLock)" />
    <gp-btn
      :label="t('ngp/admin_bar/force_stop')"
      size="sm"
      btn-type="danger"
      :disabled="!isStarting"
      @click="stopStarting()"
    />
  </div>
  <interactivity-lock-base v-if="toggleLock" data-testid="interactivity-lock" data-sentry-unmask>
    <ngp-toggle v-if="ctxError" />
    <gp-brand-loading class="z-1 sm:scale-150" />
    <history-log
      :current-item="currentItem"
      :current-item-icon="currentItemIcon"
      :history-items="historyItems"
      class="-mt-14 mb-4 h-32"
    />
    <div class="w-full text-center">
      <transition
        leave-to-class="opacity-0"
        enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-200 ease-in-out"
        enter-active-class="transition-opacity duration-200 ease-in-out"
      >
        <error-display v-if="ctxError" />
        <maintenance-lock v-else-if="maintenanceInteractivityLocked" />
        <broadcast-progress v-else-if="isBroadcasting" />
        <div v-else-if="installProgress">
          <gp-progress-fancy class="w-full" :hide-label="true" :value="installProgress"> </gp-progress-fancy>
        </div>
      </transition>
      <div v-if="consoleEnabled" class="mt-4 flex justify-center text-left">
        <console-base :messages="messages" />
      </div>
    </div>
  </interactivity-lock-base>
</template>
<script setup lang="ts">
import DialogConfirm from "@/components/dialogs/DialogConfirm.vue";
import { useDialogStore } from "@/store/dialog";
import { faWrench } from "@fortawesome/free-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { GpBrandLoading, GpBtn, GpProgressFancy } from "@gportal/uikit";
import { type ComputedRef, computed, markRaw, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { type InstallProgress, type Maybe, Service_States } from "../../graphql";
import { useMaintenanceStore } from "../../store/maintenance";
import { CtxLoadError, useServiceStore } from "../../store/service";
import { usePermissions } from "../../util/permissions";
import useServiceState from "../../util/serviceState";
import NgpToggle from "../util/NgpToggle.vue";
import ConsoleBase from "../views/console/ConsoleBase.vue";
import HistoryLog from "./HistoryLog.vue";
import InteractivityLockBase from "./InteractivityLockBase.vue";
import BroadcastProgress from "./content/BroadcastProgress.vue";
import ErrorDisplay from "./content/ErrorDisplay.vue";
import MaintenanceLock from "./content/MaintenanceLock.vue";
import useHistoryLog from "./historyLog";
import { useClientStore } from "@/store/client";

const { t } = useI18n();
const service = useServiceStore();
const client = useClientStore();
const maintenance = useMaintenanceStore();
const { isAdmin } = usePermissions();
const { stateLabel } = useServiceState();
const dialog = useDialogStore();

const maintenanceInteractivityLocked = computed(() => maintenance.maintenanceInteractivityLocked);
const { currentItem, currentItemIcon, historyItems, updateItem } = useHistoryLog(4);
const isLoading = computed(() => service.isLoading);
const currentState = computed(() => service.ctx.service?.currentState);
const fsmState = computed(() => currentState.value?.fsmState);
const isStarting = computed(() => fsmState.value == "Starting");

const isUpdating = computed(() => currentState?.value?.state === Service_States.Updating);
const ctxError: ComputedRef<CtxLoadError | undefined> = computed(() => service.ctxLoadError);
const toggleLock = computed(() => client.interactivityLockVisibility);
const setToggleLock = (value: boolean) => client.setInteractivityLockVisibility(value);

const messages = computed(() => service.consoleMessages);
const consoleEnabled = computed(() => maintenance.consoleEnabled);

watchEffect(() => {
  if (isLoading.value) {
    updateItem(t("ngp/loading/message"));
  } else if (ctxError.value) {
    if (ctxError.value.type === "not-found") {
      updateItem(t("ngp/load_error/not_found/title"), false, faXmark, faXmark);
    } else {
      updateItem(t("ngp/load_error/error/title"), true, undefined, faXmark);
    }
  } else if (maintenanceInteractivityLocked.value) {
    updateItem(t("ngp/maintenance_lock/title"));
  } else {
    updateItem(stateLabel.value);
  }
});

const isBroadcasting = computed(() => fsmState.value === "BroadcastingShutdown");
const installProgress = computed(
  () => (currentState.value?.fsmStateLiveProgress as Maybe<InstallProgress>)?.percentage,
);

async function stopStarting() {
  const confirm = await dialog.createDialog({
    component: markRaw(DialogConfirm),
    props: {
      headerIcon: faWrench,
      aboveInteractivityLock: true,
      headerText: t("ngp/action/force_stop/warning"),
      contentText: t("ngp/restart/action/deleteall/title"),
    },
  });
  if (confirm && isStarting.value) await service.stopService(true);
}
</script>
